import type {
  ChatMessage,
  SendMessageRequest,
  SendMessageResponse,
  OrchestratorConversation,
} from "../types/conversationTypes";
import { useAuthStore } from "@/stores/authStore";

// WS base
const WS_BASE =
  import.meta.env.VITE_AGENTIC_WS_URL;
const LS_UID_KEY = "agentic_uid";

function getUserId(): string {
  const userId = useAuthStore.getState().user?.id;
  
  if (userId) {
    localStorage.setItem(LS_UID_KEY, userId);
    return userId;
  }

  const uuid = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 10);

  const guest = `guest-${uuid}`;
  localStorage.setItem(LS_UID_KEY, guest);
  return guest;
}

function getToken(): string {
  const token = useAuthStore.getState().token;
  if (token && token !== "undefined" && token !== "null") return token;
  return "guest-123";
}

let socket: WebSocket | null = null;
let isOpen = false;
let pending: Array<string> = [];
let convId: string | null = null;
let overrideConvIdInUrl: string | null = null;

const listeners: Array<(payload: unknown) => void> = [];

function buildUrl() {
  const url = new URL(WS_BASE);
  url.searchParams.set("userId", getUserId());
  url.searchParams.set("token", getToken());
  if (overrideConvIdInUrl) {
    url.searchParams.set("conversationId", overrideConvIdInUrl);
  }
  return url.toString();
}

function ensureSocket(): Promise<void> {
  if (
    socket &&
    (socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING)
  ) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    try {
      socket = new WebSocket(buildUrl());
    } catch (e) {
      reject(e);
      return;
    }
    socket.onopen = () => {
      isOpen = true;
      for (const msg of pending) socket!.send(msg);
      pending = [];
      console.log("[WS] Connection opened");
      resolve();
    };
    socket.onmessage = (ev) => {
      let data: unknown = ev.data;
      if (typeof data === "string") {
        try {
          if (data.trim().startsWith("{") || data.trim().startsWith("[")) {
            data = JSON.parse(data);
          } else {
            console.warn("Non-JSON WebSocket message:", data);
          }
        } catch (e) {
          console.error("Error parsing WebSocket message:", e, data);
        }
      }

      // console.log("[WS] Received data:", data);
      for (const cb of [...listeners]) cb(data);
      const obj: any = data;
      if (obj && obj.id && obj.messages && !convId) {
        convId = obj.id;
        // console.log("[WS] Initial conversation id:", convId);
      }
    };
    socket.onerror = () => {
      isOpen = false;
      console.error("WebSocket error occurred");
    };
    socket.onclose = () => {
      isOpen = false;
      socket = null;
      convId = null;
      console.log("[WS] Connection closed");
    };
  });
}

function resetSocket() {
  if (socket) {
    try {
      socket.close();
    } catch {}
  }
  socket = null;
  isOpen = false;
  convId = null;
  overrideConvIdInUrl = null;
  pending = [];
  console.log("[WS] Socket reset requested");
}

function onceMessage<T = unknown>(
  predicate: (d: any) => boolean,
  timeoutMs = 90000
): Promise<T> {
  return new Promise((resolve, reject) => {
    const handler = (d: any) => {
      try {
        if (predicate(d)) {
          clearTimeout(timer);
          listeners.splice(listeners.indexOf(handler), 1);
          resolve(d as T);
        }
      } catch (e) {
        console.error("Error in message handler:", e);
      }
    };
    const timer = setTimeout(() => {
      listeners.splice(listeners.indexOf(handler), 1);
      reject(new Error("WS timeout"));
    }, timeoutMs);
    listeners.push(handler);
  });
}

export const wsApi = {
  async sendMessage(req: SendMessageRequest): Promise<SendMessageResponse> {
    await ensureSocket();

    if (!convId) {
      // Wawit initial conversation
      await onceMessage<any>(
        (d) => d && typeof d === "object" && "id" in d && "messages" in d
      );
    }

    if (!isOpen || !socket) throw new Error("WS not connected");
    // console.log("[WS] Sending message:", req.message);
    socket.send(req.message);

    // Wait orchestrator response
    const resp = await onceMessage<SendMessageResponse>((d) => {
      if (!d || typeof d !== "object") return false;
      return "conversationId" in d && "messages" in d;
    });

    // console.log("[WS] Response:", resp);
    const messages: ChatMessage[] = [];

    resp.messages.forEach((m) => {
      messages.push({
        content: m.content,
        timestamp: new Date(m.timestamp),
        images: m.images || [],
        options: m.options,
      });
    });
    
    return { conversationId: resp.conversationId, messages, status: resp.status };
  },

  async getConversations() {
    return [] as Array<{ id: string; title: string; date: string }>;
  },

  async getConversation(_id: string) {
    return [] as ChatMessage[];
  },

  async updateConversation(): Promise<OrchestratorConversation> {
    throw new Error("Not implemented over WS");
  },

  async deleteConversation() {
    return true;
  },
  async deleteAllConversations() {
    return true;
  },
  reset() {
    resetSocket();
  },
  switchConversation(conversationId: string) {
    // Set override and force reconnection
    overrideConvIdInUrl = conversationId;
    resetSocket();
    console.log("[WS] Switching to conversation", conversationId);
  },
};
